import { useLayoutEffect, useState } from "react";
import { Z_INDEX } from "../../ui/zIndex.js";

/**
 * Positioniert ein Dropdown per fixed + Portal (Modal/overflow-safe).
 */
export function usePortaledMenuPosition(anchorRef, open, { gap = 6, maxHeight = 360 } = {}) {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") {
      setStyle(null);
      return undefined;
    }

    const update = () => {
      const el = anchorRef?.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewTop = vv?.offsetTop ?? 0;
      const viewHeight = vv?.height ?? window.innerHeight;
      const viewBottom = viewTop + viewHeight;

      const spaceBelow = viewBottom - rect.bottom - gap - 12;
      const spaceAbove = rect.top - viewTop - gap - 12;
      const placeAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      const available = Math.max(140, placeAbove ? spaceAbove : spaceBelow);
      const height = Math.min(maxHeight, available);
      const rawTop = placeAbove ? rect.top - height - gap : rect.bottom + gap;
      const top = Math.min(Math.max(viewTop + 8, rawTop), viewBottom - height - 8);

      setStyle({
        position: "fixed",
        top,
        left: rect.left,
        width: Math.max(rect.width, 280),
        maxHeight: height,
        zIndex: Z_INDEX.selectPortal,
      });
    };

    update();

    const opts = { passive: true };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, opts);
    window.visualViewport?.addEventListener("resize", update, opts);
    window.visualViewport?.addEventListener("scroll", update, opts);

    const scrollParents = [];
    let parent = anchorRef?.current?.parentElement;
    while (parent) {
      const { overflowY, overflow } = getComputedStyle(parent);
      if (/(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflow)) {
        parent.addEventListener("scroll", update, opts);
        scrollParents.push(parent);
      }
      parent = parent.parentElement;
    }

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, opts);
      window.visualViewport?.removeEventListener("resize", update, opts);
      window.visualViewport?.removeEventListener("scroll", update, opts);
      scrollParents.forEach((node) => node.removeEventListener("scroll", update, opts));
    };
  }, [open, anchorRef, gap, maxHeight]);

  return style;
}
